<template>
  <!-- One game on the Picks page, always read from the app's side (the row's
       `side`). Left rail: grey before kickoff, amber while live, green/red once
       final. Right: points that side is covering (+) or short (-) by. -->
  <li class="row" :class="{ 'row--picked': row.pick }">
    <!-- Every row opens in place: before kickoff the pre-game read, while
         live both sides' chance to cover and the chart so far, once final the
         chart to the result -- each with who in the pool took which side.
         Insights is one more tap away, inside. -->
    <button type="button" :aria-expanded="open" class="row-link" @click="toggle">
      <span class="rail" :class="railClass" role="img" :aria-label="railLabel"></span>
      <TeamLogoChip :team="sideTeam" :abbr="sideAbbr" size="sm" class="!h-8 !w-8 sm:!h-9 sm:!w-9" />
      <span class="min-w-0">
        <span class="block truncate text-[13px] font-extrabold text-white">
          {{ row.bet }}
          <span v-if="row.winProbability != null" class="text-xs font-extrabold" :class="bandText">({{ row.winProbability.toFixed(1) }}%)</span>
        </span>
        <span class="mt-0.5 block truncate text-[11px] font-semibold text-slate-400">
          <span v-if="row.band" class="font-bold" :class="bandWord">{{ row.band }}</span>
          <span v-if="live" class="text-amber-400"> · {{ chance }} to cover</span>
        </span>
      </span>
      <!-- The score as a scoreboard: app's side on top, opponent under it
           (one line from 640px). Before kickoff just the two teams. -->
      <span class="board">
        <span v-for="team in teams" :key="team.side" class="board-team" :class="team.cls">
          <img v-if="team.logo" :src="team.logo" alt="" class="board-logo" loading="lazy" />
          <span class="board-abbr">{{ team.abbr }}</span>
          <span v-if="state !== 'pre'" class="board-score">{{ team.score }}</span>
        </span>
      </span>
      <span class="text-right">
        <!-- Before kickoff the time takes the points' place; the day under it. -->
        <span v-if="state === 'pre'" class="block whitespace-nowrap text-xs font-extrabold leading-tight text-slate-200">{{ kickoffTime }}</span>
        <span v-else class="pill" :class="cushionClass">{{ cushionText }}</span>
        <span class="mt-0.5 block whitespace-nowrap text-[10px] font-bold" :class="state === 'live' ? 'text-amber-400' : 'text-slate-400'">{{ statusText }}</span>
      </span>
    </button>
    <CoverDetail
      v-if="open && state !== 'pre'"
      class="mx-0.5 mb-2.5"
      :path="path"
      :loading="loading"
      :tone="state === 'final' ? finalTone : 'live'"
      :sides="state === 'live' ? liveSides : null"
      :what-if="whatIf"
      :pregame="row.winProbability == null ? null : row.winProbability / 100"
      :band="row.band"
    >
      <PoolCrowdSection v-if="poolView" :view="poolView" :first="row.side" :home-team="game.home_team" :away-team="game.away_team" />
      <router-link :to="`/games/${row.id}`" class="mt-2.5 inline-block text-[11px] font-bold text-slate-300 hover:text-white">Insights →</router-link>
    </CoverDetail>
    <div v-if="open && state === 'pre'" class="mx-0.5 mb-2.5 rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <div v-if="row.winProbability != null || marketChip" class="flex flex-wrap gap-1.5">
        <span v-if="row.winProbability != null" class="chip">Win {{ row.winProbability.toFixed(1) }}%<template v-if="row.band"> · {{ row.band }}</template></span>
        <span v-if="marketChip" class="chip">{{ marketChip }}</span>
      </div>
      <PoolCrowdSection v-if="poolView" :view="poolView" :first="row.side" :home-team="game.home_team" :away-team="game.away_team" />
      <router-link :to="`/games/${row.id}`" class="mt-2.5 inline-block text-[11px] font-bold text-slate-300 hover:text-white">Insights →</router-link>
    </div>
  </li>
</template>

<script>
import TeamLogoChip from "./TeamLogoChip.vue";
import CoverDetail from "./CoverDetail.vue";
import PoolCrowdSection from "./PoolCrowdSection.vue";
import api from "@/utils/api";
import { teamLogo } from "@/utils/teamLogo.js";
import { formatChance } from "@/utils/liveCover.js";
import { formatSigned } from "@/utils/edge.js";
import { clockLabel, gameState, kickoffLabel, sideCoverPath, sideLiveCover } from "@/utils/gameday.js";
import { gamePoolView } from "@/utils/poolCrowd.js";

const BAND_TEXT = { Strong: "text-strength-lime", Solid: "text-strength-lime", Slight: "text-strength-slight", Flat: "text-slate-200" };
const BAND_WORD = { Strong: "text-strength-lime", Solid: "text-strength-lime", Slight: "text-strength-slight", Flat: "text-slate-400" };

export default {
  name: "GamedayRow",
  components: { TeamLogoChip, CoverDetail, PoolCrowdSection },
  props: {
    // A `gameRow` from utils/weekGames.js, carrying `pick` when it is yours.
    row: { type: Object, required: true },
    now: { type: Number, default: () => Date.now() },
    // The week's /pool_crowd payload and utils/poolCrowd.js gameOutcomes,
    // shared by every row; null until the page has them.
    crowd: { type: Object, default: null },
    outcomes: { type: Array, default: () => [] },
  },
  data() {
    // `plays` is ESPN's scoring plays for this game, fetched on first open;
    // the path is rebuilt from them as the row's live score and clock move.
    return { open: false, plays: null, loading: false };
  },
  computed: {
    scoreKey() {
      return `${this.game.home_score}-${this.game.away_score}-${this.game.status}`;
    },
    // Only an open row pays for its pool section: an open game's swing tiles
    // play the rest of the week out a few hundred times each.
    poolView() {
      if (!this.open || !this.crowd) return null;
      return gamePoolView(this.crowd, this.outcomes, this.row.id);
    },
    finalTone() {
      if (this.row.result === "win") return "win";
      if (this.row.result === "loss") return "loss";
      // A push: not the live colour, but still a finished path (CoverDetail
      // leaves its last point out of the low/high for anything but "live").
      return "push";
    },
    // "Frozen +3 · market +2.5", from the row's side.
    marketChip() {
      if (this.row.marketLine == null || this.game.league_spread == null) return null;
      const line = this.row.side === "home" ? Number(this.game.league_spread) : -Number(this.game.league_spread);
      return `Frozen ${formatSigned(line)} · market ${formatSigned(Math.round(this.row.marketLine * 2) / 2)}`;
    },
    // Both sides of the frozen line, the row's side first. On an integer line
    // the two do not add to 100%: a push scores nothing for either.
    liveSides() {
      const side = (s) => {
        const home = Number(this.game.league_spread);
        const line = this.game.league_spread == null ? null : (s === "home" ? home : -home);
        const live = sideLiveCover(this.game, s);
        return {
          abbr: s === "home" ? this.game.home_abbr : this.game.away_abbr,
          line: line == null ? "" : formatSigned(line),
          p: live ? live.p : null,
        };
      };
      return [side(this.row.side), side(this.otherSide)];
    },
    whatIf() {
      if (!this.live) return null;
      return `${this.sideAbbr} TD → ${formatChance(this.live.ifSideScores)} · ${this.opponentAbbr} TD → ${formatChance(this.live.ifOpponentScores)}`;
    },
    path() {
      return this.plays ? sideCoverPath(this.game, this.row.side, this.plays, this.row) : null;
    },
    game() {
      return this.row.game;
    },
    state() {
      return gameState(this.game);
    },
    otherSide() {
      return this.row.side === "home" ? "away" : "home";
    },
    sideAbbr() {
      return this.row.side === "home" ? this.game.home_abbr : this.game.away_abbr;
    },
    sideTeam() {
      return this.row.side === "home" ? this.game.home_team : this.game.away_team;
    },
    opponentAbbr() {
      return this.otherSide === "home" ? this.game.home_abbr : this.game.away_abbr;
    },
    live() {
      return sideLiveCover(this.game, this.row.side, this.row);
    },
    chance() {
      return this.live ? formatChance(this.live.p) : null;
    },
    kickoff() {
      return kickoffLabel(this.game.kickoff_at, this.now);
    },
    // "4:25 PM" on top, and the weekday under it unless it is today.
    kickoffTime() {
      return this.kickoff.split(" ").slice(-2).join(" ");
    },
    statusText() {
      if (this.state === "pre") return this.kickoff.split(" ").length > 2 ? this.kickoff.split(" ")[0] : "";
      return this.state === "final" ? "Final" : clockLabel(this.game);
    },
    // Top: the side the row is about. Bottom: its opponent, bold and white
    // when that is the side you took. The winner's score is the bright one.
    teams() {
      const row = (side) => {
        const other = side === "home" ? "away" : "home";
        const leads = Number(this.score(side)) >= Number(this.score(other));
        const yours = this.row.pick && this.row.pick.side === side && side !== this.row.side;
        return {
          side,
          abbr: side === "home" ? this.game.home_abbr : this.game.away_abbr,
          logo: teamLogo(side === "home" ? this.game.home_abbr : this.game.away_abbr),
          score: this.score(side),
          cls: [this.state !== "pre" && leads ? "board-team--lead" : "", yours ? "board-team--yours" : ""],
        };
      };
      return [row(this.row.side), row(this.otherSide)];
    },
    cushion() {
      if (this.state === "final") return this.row.margin;
      if (this.state === "live") return this.row.liveMargin;
      return null;
    },
    cushionText() {
      const value = this.cushion;
      if (value == null) return "—";
      if (value === 0) return "0";
      return `${value > 0 ? "+" : "−"}${Math.abs(value)}`;
    },
    cushionClass() {
      if (this.cushion == null || this.cushion === 0) return "bg-slate-400/15 text-slate-400";
      return this.cushion > 0 ? "bg-emerald-400/15 text-emerald-400" : "bg-rose-400/15 text-rose-400";
    },
    railClass() {
      if (this.state === "live") return "bg-amber-400";
      if (this.state === "final" && this.row.margin != null) {
        if (this.row.margin === 0) return "bg-slate-400";
        return this.row.margin > 0 ? "bg-emerald-400" : "bg-rose-400";
      }
      return "bg-slate-500/30";
    },
    railLabel() {
      if (this.state === "live") return "In progress";
      if (this.state === "final") return { win: "Covered", loss: "Missed", push: "Push" }[this.row.result] || "Final";
      return "Not started";
    },
    bandText() {
      return BAND_TEXT[this.row.band] || "text-slate-300";
    },
    bandWord() {
      return BAND_WORD[this.row.band] || "text-slate-400";
    },
  },
  watch: {
    // A live game's plays go stale while the row stays open: refetch whenever
    // the page's poll brings a new score (the server caches them a minute).
    scoreKey() {
      if (this.open && this.state === "live") this.fetchPlays();
    },
  },
  methods: {
    toggle() {
      this.open = !this.open;
      if (this.open && !this.plays && this.state !== "pre") this.fetchPlays();
    },
    async fetchPlays() {
      this.loading = !this.plays;
      try {
        const { data } = await api.get(`/api/v1/games/${this.row.id}/scoring_plays`);
        this.plays = data.scoring_plays || [];
      } catch {
        this.plays = this.plays || [];
      } finally {
        this.loading = false;
      }
    },
    score(side) {
      return side === "home" ? this.game.home_score : this.game.away_score;
    },
  },
};
</script>

<style scoped>
.row { border-bottom: 1px solid rgb(255 255 255 / 0.07); }
/* Today's picked-row tint (GameResultRow). */
.row--picked { background: rgb(56 189 248 / 0.1); }
.row-link { width: 100%; text-align: left; display: grid; grid-template-columns: 4px auto minmax(0, 1fr) auto 58px; align-items: center; column-gap: 0.45rem; padding: 0.55rem 0.2rem; }
/* Points covering (+) or short (-), as a soft green/red capsule. */
.pill { display: inline-block; padding: 0.15rem 0.45rem; border-radius: 9999px; font-size: 13px; font-weight: 800; line-height: 1.25; white-space: nowrap; font-variant-numeric: tabular-nums; }
.rail { align-self: stretch; border-radius: 9999px; }
.chip { border-radius: 9999px; background: rgb(255 255 255 / 0.06); padding: 0.25rem 0.5rem; color: #cbd5e1; font-size: 10px; font-weight: 700; white-space: nowrap; }
.board { display: grid; row-gap: 0.15rem; }
.board-team { display: grid; grid-template-columns: 16px 2.1rem 1.35rem; align-items: center; column-gap: 0.3rem; color: #94a3b8; font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums; }
.board-logo { height: 16px; width: 16px; }
.board-score { text-align: right; font-weight: 800; }
.board-team--lead { color: #fff; }
.board-team--yours .board-abbr { color: #fff; font-weight: 900; text-decoration: underline; text-underline-offset: 2px; }
/* With room to spare: one line, "IND 23 – 41 BAL", centred in its own column. */
@media (min-width: 640px) {
  .row-link { grid-template-columns: 4px auto minmax(0, 1fr) minmax(0, 1fr) 96px; column-gap: 0.75rem; padding: 0.6rem 0.5rem; }
  .board { display: flex; justify-content: center; align-items: center; gap: 0.6rem; font-size: 15px; }
  .board-team { display: flex; gap: 0.35rem; font-size: 15px; }
  .board-team:last-child { flex-direction: row-reverse; }
  .board-team:first-child::after { content: "–"; color: #64748b; margin-left: 0.6rem; }
  .board-logo { height: 20px; width: 20px; }
}
</style>
